import type { IntegrationId } from '../shared/integrations/types'

export type SetupStepItem =
  | string
  | {
      text: string
      displayText?: string
      copyText?: string
    }

export type SetupStep = {
  title: string
  description?: string
  items?: SetupStepItem[]
  code?: string
  copyCode?: string
  windowsCode?: string
  windowsCopyCode?: string
  note?: string
  link?: {
    text: string
    url: string
  }
}

type SetupMethod = {
  id: string
  label: string
  steps: SetupStep[]
}

export type IntegrationConfig = {
  description: string
  exampleUseCases: string
  setupMethods?: SetupMethod[]
  examplePrompts?: string[]
  requiresApiKey?: boolean
}

export const INTEGRATION_CONFIG: Record<IntegrationId, IntegrationConfig> = {
  zapier: {
    description:
      'Zapier supercharges Buffer with 5,000+ no-code integrations to automate your entire workflow.',
    exampleUseCases:
      'Picture this: auto-post Buffer updates to Discord communities, pipe engagement stats into Looker Studio dashboards for real-time insights, or instantly transform Trello card updates into ready-to-schedule Buffer drafts - unlocking pro-level efficiency!',
    requiresApiKey: false,
    setupMethods: [
      {
        id: 'zapier',
        label: 'Setup',
        steps: [
          {
            title: 'Navigate to Zapier Connections',
            description: 'Click "Add connection" to connect Buffer.',
            link: {
              text: 'Zapier Connections',
              url: 'https://zapier.com/app/assets/connections/apps',
            },
          },
          {
            title: 'Search for MCP Client',
            description:
              'Search for "MCP Client by Zapier" and select it, then click "Add connection".',
          },
          {
            title: 'Configure the connection',
            description: 'Fill in the form with the following settings:',
            items: [
              { text: 'Server URL:', copyText: 'MCP_SERVER_URL' },
              'Transport: Streamable HTTP',
              'OAuth: Yes',
            ],
          },
          {
            title: 'Connect and approve access',
            description:
              'Click "Yes, Continue to MCP Client by Zapier", then sign in to Buffer if prompted and approve access to complete the connection.',
          },
        ],
      },
    ],
    examplePrompts: [
      'When a new blog post is published in WordPress, create a draft post in Buffer with the post title and link',
      'When a new row is added to my Google Sheet, add a post to my Buffer queue with that content',
      'Every Monday at 9am, check my Buffer queue and notify me in Slack if any channel has no posts scheduled',
    ],
  },
  claude: {
    description:
      'Claude lets you manage your Buffer content using natural language.',
    exampleUseCases:
      'Create posts, schedule content, and manage your social media directly from Claude or Claude Code using the Buffer MCP server.',
    requiresApiKey: false,
    setupMethods: [
      {
        id: 'claude_desktop',
        label: 'Claude Web/Desktop',
        steps: [
          {
            title: 'Go to Claude',
            description: 'Open Claude in your browser or desktop app.',
            link: {
              text: 'claude.ai',
              url: 'https://claude.ai/',
            },
          },
          {
            title: 'Open Connectors',
            description: 'Navigate to "Customize", then click "Connectors".',
          },
          {
            title: 'Search for Buffer',
            description:
              'Search for "Buffer" in the connector list, then select it.',
          },
          {
            title: 'Connect and approve access',
            description:
              'Click "Connect". Sign in to Buffer if prompted and approve access to complete the connection.',
          },
        ],
      },
      {
        id: 'claude_code',
        label: 'Claude Code',
        steps: [
          {
            title: 'Install Claude Code',
            description: "Follow Anthropic's quickstart guide for Claude Code.",
            link: {
              text: 'Install Claude Code',
              url: 'https://code.claude.com/docs/en/quickstart',
            },
          },
          {
            title: 'Add the Buffer MCP server',
            description:
              'Run the following command to add the Buffer MCP server to Claude Code:',
            code: `claude mcp add --transport http buffer MCP_SERVER_URL`,
          },
          {
            title: 'Authenticate with Buffer',
            description:
              'Start a Claude Code session, run the following command, select the Buffer MCP server, then sign in to Buffer if prompted and approve access.',
            code: `/mcp`,
          },
        ],
      },
    ],
    examplePrompts: [
      'Show me all my scheduled Buffer posts for this week',
      "Create a draft post in Buffer that says 'We just launched our redesigned dashboard!' for my X channel",
      'List my Buffer channels and show me which ones have posts scheduled for tomorrow',
    ],
  },
  chatgpt: {
    description:
      'ChatGPT lets you manage your Buffer content using natural language.',
    exampleUseCases:
      'Create and schedule posts, get content suggestions, and manage your social media directly from ChatGPT or the Codex CLI.',
    requiresApiKey: false,
    setupMethods: [
      {
        id: 'chatgpt_web',
        label: 'ChatGPT Web',
        steps: [
          {
            title: 'Go to ChatGPT',
            description: 'Open ChatGPT in your browser.',
            link: {
              text: 'chatgpt.com',
              url: 'https://chatgpt.com/',
            },
          },
          {
            title: 'Enable Developer mode',
            description:
              'Open Settings, find the Apps/Connectors section, then toggle on Developer mode under Advanced settings.',
          },
          {
            title: 'Add a new app',
            description: 'Add a new app and fill in the form with:',
            items: [
              'Name: Buffer',
              { text: 'MCP Server URL:', copyText: 'MCP_SERVER_URL' },
              'Authentication: OAuth',
            ],
          },
          {
            title: 'Create and approve access',
            description: 'Click "Create" to finish.',
            note: "A Buffer sign-in window may open if you're not already logged in. Approve access to complete the connection.",
          },
        ],
      },
      {
        id: 'codex_cli',
        label: 'Codex CLI',
        steps: [
          {
            title: 'Install Codex CLI',
            description: "Follow OpenAI's install guide for Codex CLI.",
            link: {
              text: 'Install Codex CLI',
              url: 'https://developers.openai.com/codex/cli',
            },
          },
          {
            title: 'Add the Buffer MCP server',
            description:
              'In your terminal, run the following command to add the Buffer MCP server to Codex:',
            code: `codex mcp add buffer --url MCP_SERVER_URL`,
            note: 'A browser window opens. Sign in to Buffer if prompted and approve access.',
          },
        ],
      },
    ],
    examplePrompts: [
      'Show me all my scheduled Buffer posts for this week',
      "Create a draft post in Buffer that says 'We just launched our redesigned dashboard!' for my X channel",
      'List my Buffer channels and show me which ones have posts scheduled for tomorrow',
    ],
  },
  notion: {
    description:
      'Notion lets you manage your Buffer content using natural language.',
    exampleUseCases:
      'Draft posts, schedule content, and manage your social media from a Notion custom agent using the Buffer MCP server.',
    requiresApiKey: false,
    setupMethods: [
      {
        id: 'notion',
        label: 'Setup',
        steps: [
          {
            title: 'Enable custom MCP servers',
            description:
              'In Notion, go to Settings, then Connections, and open the Manage tab. Enable "Custom MCP servers".',
          },
          {
            title: 'Open a custom agent',
            description:
              'Open or create the custom agent you want to connect, then click "Settings".',
          },
          {
            title: 'Add the Buffer MCP server',
            description:
              'Click "Add connection", then "Add custom MCP", and fill in the form with the following details:',
            items: [
              { text: 'Server URL:', copyText: 'MCP_SERVER_URL' },
              'Name: Buffer MCP',
              'Auth method: OAuth',
            ],
          },
          {
            title: 'Connect and approve access',
            description:
              'Click "Connect", then sign in to Buffer if prompted and approve access to complete the connection.',
          },
        ],
      },
    ],
    examplePrompts: [
      'Show me all my scheduled Buffer posts for this week',
      "Create a draft post in Buffer that says 'We just launched our redesigned dashboard!' for my X channel",
      'List my Buffer channels and show me which ones have posts scheduled for tomorrow',
    ],
  },
  manus: {
    description:
      'Manus lets you manage your Buffer content using natural language.',
    exampleUseCases:
      'Ask Manus to draft a post, schedule it, and manage your social media without leaving the chat.',
    requiresApiKey: false,
    setupMethods: [
      {
        id: 'manus',
        label: 'Setup',
        steps: [
          {
            title: 'Open Connectors',
            description:
              'In Manus, go to Settings, then Connectors, click "Create", and select "Custom MCP".',
          },
          {
            title: 'Add the Buffer MCP server',
            description:
              'Fill in the form with the following details, then click "Save":',
            items: [
              'Server Name: Buffer MCP Server',
              'Transport: HTTP',
              { text: 'Server URL:', copyText: 'MCP_SERVER_URL' },
            ],
          },
          {
            title: 'Sign in and approve access',
            description:
              'The first time you use the Buffer MCP server, Manus asks you to log in. Sign in to Buffer if prompted and approve access to connect your account.',
          },
        ],
      },
    ],
    examplePrompts: [
      'List all my connected Buffer channels',
      "Add a post to my Buffer queue that says 'Excited to share our latest update!' for next Monday",
      'Show me my Buffer posts scheduled for this week, grouped by channel',
    ],
  },
  perplexity: {
    description:
      'Perplexity lets you manage your Buffer content using natural language.',
    exampleUseCases:
      'Ask Perplexity to research a topic, draft a post about it, and add it to your Buffer queue without leaving the conversation.',
    requiresApiKey: false,
    setupMethods: [
      {
        id: 'perplexity_web',
        label: 'Perplexity Web',
        steps: [
          {
            title: 'Open Perplexity',
            description: 'Open Perplexity in your browser.',
            link: {
              text: 'perplexity.ai',
              url: 'https://www.perplexity.ai/',
            },
          },
          {
            title: 'Open Connectors',
            description:
              'Go to "All settings", then "Connectors", and add a "Custom connector".',
          },
          {
            title: 'Add the custom connector',
            description:
              'Fill in the form, check the "I understand custom connectors can introduce..." consent checkbox, and click "Add":',
            items: [
              'Name: Buffer',
              { text: 'MCP Server URL:', copyText: 'MCP_SERVER_URL' },
            ],
          },
          {
            title: 'Add the connector',
            description:
              'Under the "Custom" connectors list, find Buffer and click "Add connector".',
          },
          {
            title: 'Sign in and approve access',
            description:
              'Sign in to Buffer if prompted and approve access to complete the connection.',
          },
        ],
      },
      {
        id: 'perplexity_desktop',
        label: 'Perplexity Desktop',
        steps: [
          {
            title: 'Open Connectors in Perplexity Desktop',
            description:
              'Open the desktop app, go to "Settings", then "Connectors", and click "Add connector".',
          },
          {
            title: 'Add the server config',
            description:
              'Open the "Advanced" tab, then set the Server Name and paste the configuration below, then click "Save":',
            items: ['Server Name: Buffer'],
            code: `{
  "command": "npx",
  "args": [
    "-y",
    "mcp-remote",
    "MCP_SERVER_URL"
  ]
}`,
            note: "Requires Node.js. Perplexity needs to find npx on your system. If the connector won't start, point the config at the full path of your Node install.",
          },
          {
            title: 'Sign in and approve access',
            description:
              'Perplexity Desktop will open the OAuth consent screen. Sign in to Buffer if prompted and approve access.',
          },
        ],
      },
    ],
    examplePrompts: [
      'List all my connected Buffer channels',
      "Add a post to my Buffer queue that says 'Excited to share our latest update!' for next Monday",
      "Show me all my draft posts in Buffer so I can review what's pending",
    ],
  },
  mcp: {
    description:
      "Use our open MCP server to connect any MCP-compatible AI tool to Buffer. If your AI tool supports MCP but doesn't have a Buffer integration guide, follow the setup instructions.",
    exampleUseCases: '',
    setupMethods: [
      {
        id: 'mcp',
        label: 'Setup',
        steps: [
          {
            title: 'Configure Your MCP Client',
            description:
              'LLM clients that support MCP and headers can connect to Buffer by adding an HTTP MCP server with the following settings:',
            items: [
              { text: 'Server URL:', copyText: 'MCP_SERVER_URL' },
              {
                text: 'Authorization Header:',
                copyText: 'Authorization: Bearer API_KEY',
              },
            ],
            note: 'Replace API_KEY with your API key from Step 1.',
          },
        ],
      },
    ],
    examplePrompts: [
      'List all my connected Buffer channels',
      "Add a post to my Buffer queue that says 'Excited to share our latest update!' for next Monday",
      "Show me all my draft posts in Buffer so I can review what's pending",
    ],
  },
  grok: {
    description:
      'Grok lets you manage your Buffer content using natural language.',
    exampleUseCases:
      'Ask Grok to draft a post, schedule it, and manage your social media without leaving the chat.',
    requiresApiKey: false,
    setupMethods: [
      {
        id: 'grok',
        label: 'Setup',
        steps: [
          {
            title: 'Go to Grok',
            description: 'Open Grok in your browser.',
            link: {
              text: 'grok.com',
              url: 'https://grok.com/',
            },
          },
          {
            title: 'Open Plugins',
            description: 'Open "Plugins".',
          },
          {
            title: 'Search for Buffer',
            description:
              'Search for "Buffer" in the plugin list, then select it.',
          },
          {
            title: 'Connect and approve access',
            description:
              'Click "Connect". Sign in to Buffer if prompted and approve access to complete the connection.',
          },
        ],
      },
    ],
    examplePrompts: [
      'List all my connected Buffer channels',
      "Add a post to my Buffer queue that says 'Excited to share our latest update!' for next Monday",
      'Show me my Buffer posts scheduled for this week, grouped by channel',
    ],
  },
  cursor: {
    description:
      'Cursor lets you manage Buffer directly from your code editor.',
    exampleUseCases:
      'Build automation scripts, create posts, and manage your social media workflow without leaving your development environment.',
    requiresApiKey: false,
    setupMethods: [
      {
        id: 'cursor',
        label: 'Setup',
        steps: [
          {
            title: 'Open Cursor Settings',
            description:
              'In Cursor, open Preferences, then "Cursor Settings", then the "Tools & MCPs" tab.',
          },
          {
            title: 'Add a new MCP server',
            description:
              'Click "New MCP Server" and add the following configuration:',
            code: `{
  "mcpServers": {
    "buffer": {
      "url": "MCP_SERVER_URL"
    }
  }
}`,
          },
          {
            title: 'Connect and approve access',
            description:
              'Click "Connect", then sign in to Buffer if prompted and approve access to complete the connection.',
          },
        ],
      },
    ],
    examplePrompts: [
      'List all my Buffer channels and show which ones have posts scheduled today',
      'Create a draft post in Buffer for my LinkedIn channel announcing the release described in this CHANGELOG file',
      'Show me my Buffer posts scheduled for this week, grouped by channel',
    ],
  },
  antigravity: {
    description:
      "Antigravity lets you manage Buffer directly from Google's agent-first IDE.",
    exampleUseCases:
      'Draft posts, check your queue, and manage your social media workflow without leaving your IDE.',
    requiresApiKey: false,
    setupMethods: [
      {
        id: 'antigravity',
        label: 'Setup',
        steps: [
          {
            title: 'Add the Buffer MCP server to your config',
            description:
              'Open ~/.gemini/config/mcp_config.json and add the following configuration:',
            code: `{
  "mcpServers": {
    "buffer": {
      "serverUrl": "MCP_SERVER_URL"
    }
  }
}`,
          },
          {
            title: 'Restart Antigravity',
            description:
              'Quit Antigravity and open it again so it picks up the new server.',
          },
          {
            title: 'Authenticate Buffer',
            description:
              'Go to Settings, then Customizations, find Buffer in the MCP servers list, and click it to authenticate.',
          },
          {
            title: 'Connect and approve access',
            description:
              'Sign in to Buffer if prompted and approve access. Copy the code Antigravity shows, paste it back into Antigravity, then refresh the Installed MCP Servers list to finish.',
          },
        ],
      },
    ],
    examplePrompts: [
      'List all my connected Buffer channels',
      'Draft a Buffer post announcing the feature I just shipped and schedule it for tomorrow morning',
      'Show me my Buffer posts scheduled for this week, grouped by channel',
    ],
  },
  n8n: {
    description:
      'n8n is a workflow automation tool that connects Buffer with hundreds of apps.',
    exampleUseCases:
      'Create complex automation workflows, trigger posts based on events, and integrate Buffer into your existing automation pipelines.',
    setupMethods: [
      {
        id: 'n8n',
        label: 'Setup',
        steps: [
          {
            title: 'Add MCP Client to Workflow',
            description: 'Inside of a workflow, add an "MCP Client" node.',
          },
          {
            title: 'Configure the MCP Client',
            description: 'Fill in the form with the following details:',
            items: [
              'Server Transport: HTTP Streamable',
              {
                text: 'MCP Endpoint URL:',
                copyText: 'MCP_SERVER_URL',
              },
              'Authentication: Bearer Auth',
            ],
          },
          {
            title: 'Add Your Credentials',
            description: 'Click on "Credential for Bearer Auth" and configure:',
            items: [
              'Select "Create new credential"',
              { text: 'Add your API key:', copyText: 'API_KEY' },
              'Click "Save"',
              'Close the modal',
            ],
            note: 'Replace API_KEY with your API key from Step 1.',
          },
          {
            title: 'Select Buffer MCP Tools',
            description:
              'Select the MCP tool you want the workflow to use from the available Buffer MCP tools, then configure it as needed.',
          },
        ],
      },
    ],
    examplePrompts: [
      'When a new RSS feed item appears, automatically create a draft post in Buffer with the article title and link',
      'Every Friday, pull my scheduled posts in Buffer for the next week and send a summary to Slack',
      'When a form submission comes in, create a draft post in Buffer using the submitted content',
    ],
  },
  raycast: {
    description:
      'Raycast lets you quickly access Buffer from your menu bar on macOS.',
    exampleUseCases:
      'Create posts, view your queue, and manage your social media with keyboard shortcuts and quick actions.',
    requiresApiKey: false,
    setupMethods: [
      {
        id: 'raycast',
        label: 'Setup',
        steps: [
          {
            title: 'Open Install MCP Server',
            description:
              'In Raycast, search for "Install MCP Server" and press Enter.',
            note: 'Requires Raycast Pro, since MCP servers run through Raycast AI.',
          },
          {
            title: 'Configure the Server',
            description: 'Fill in the form with the following details:',
            items: [
              'Name: Buffer',
              'Transport: HTTP',
              { text: 'URL:', copyText: 'MCP_SERVER_URL' },
            ],
          },
          {
            title: 'Install and approve access',
            description:
              'Click "Install" (or press Cmd+Enter), then sign in to Buffer if prompted and approve access to complete the connection.',
          },
        ],
      },
    ],
    examplePrompts: [
      "Add a post saying 'Just shipped a new feature! Stay tuned for details.' to my Buffer queue",
      'Show me my upcoming scheduled Buffer posts for this week',
      "Create a draft post in Buffer for each of my connected channels with the text 'Happy Monday! What are you working on this week?'",
    ],
  },
}
